// @ts-strict-ignore
import Form from 'antd/lib/form';
import FormItem from 'antd/lib/form/FormItem';
import useForm from 'antd/lib/form/hooks/useForm';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import * as React from 'react';
import { AppPlatform, AppStoreAvailableBuild, AppStoreType } from '@/types';
import Text from 'antd/lib/typography/Text';
import { ReleaseComponentProps } from '@/typescriptHelpers';
import { forEach, reduce } from 'lodash';
import { AppStoreBuildSelect } from './AppStoreBuildSelect';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

export interface BuildSelectionField {
    name: string;
    label: string;
    // Shows the required asterisk only; antd's `required` adds no validation rule
    required: boolean;
    multiple: boolean;
    showRecentBuilds: boolean;
    showPastBuildsEmptyState: boolean;
    showsClearButton?: boolean;
    currentSelection?: AppStoreAvailableBuild[];
    // Builds already picked in this other field are shown disabled here, so the same build can't be picked twice
    excludeBuildsSelectedInField?: string;
    excludeReason?: string;
    labelTooltip?: React.ReactNode;
}

interface ChangeSelectedBuildModalBaseProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    title: string;
    isLoading: boolean;
    includeAssociatedCIBuilds: boolean;
    open: boolean;
    fields: BuildSelectionField[];
    availableBuilds: AppStoreAvailableBuild[];
    onSaveChanges: (buildIdsByFieldName: Record<string, string[]>) => void;
    renderRetainedBuildsBanner?: (buildIdsByFieldName: Record<string, string[]>) => React.ReactNode;
    onClose: () => void;
    onSuccess: () => void;
}

const toBuildIds = (value: string | string[]): string[] => {
    if (!value) {
        return [];
    }

    return typeof value === 'string' ? [value] : value;
};

const ChangeSelectedBuildModalBase: React.FC<ChangeSelectedBuildModalBaseProps> = (
    props: ChangeSelectedBuildModalBaseProps,
) => {
    const [form] = useForm();
    const formValues = Form.useWatch([], form);
    const onSaveChanges = () => {
        form.validateFields().then((values) => {
            const buildIdsByFieldName = props.fields.reduce(
                (acc, field) => {
                    acc[field.name] = toBuildIds(values[field.name]);

                    return acc;
                },
                {} as Record<string, string[]>,
            );

            props.onSaveChanges(buildIdsByFieldName);
        });
    };

    React.useEffect(() => {
        if (props.open) {
            forEach(props.fields, (field) => {
                form.setFieldsValue({ [field.name]: initialValueForField(field, props.availableBuilds) });
            });
        }
    }, [props.open]);

    return (
        <Modal
            title={props.title}
            onOk={() => form.submit()}
            okButtonProps={{ loading: props.isLoading }}
            onCancel={props.onClose}
            destroyOnClose
            open={props.open}
            okText="Save changes"
            cancelText="Discard changes"
            afterClose={() => form.resetFields()}
            width={600}
        >
            <Form form={form} layout="vertical" onFinish={onSaveChanges}>
                {props.fields.map((field) => {
                    const excludedBuildIds =
                        field.excludeBuildsSelectedInField ?
                            toBuildIds(formValues?.[field.excludeBuildsSelectedInField])
                        :   [];
                    return (
                        <React.Fragment key={field.name}>
                            <FormItem
                                label={
                                    <Space>
                                        <Text>{field.label}</Text>
                                        {field.labelTooltip && <TooltipInfoIcon tooltipContent={field.labelTooltip} />}
                                    </Space>
                                }
                                name={field.name}
                                required={field.required}
                            >
                                <AppStoreBuildSelect
                                    appId={props.appId}
                                    platform={props.platform}
                                    appStoreType={props.appStoreType}
                                    availableBuilds={props.availableBuilds}
                                    includeAssociatedCIBuilds={props.includeAssociatedCIBuilds}
                                    multiple={field.multiple}
                                    showRecentBuilds={field.showRecentBuilds}
                                    showPastBuildsEmptyState={field.showPastBuildsEmptyState}
                                    showsClearButton={field.showsClearButton}
                                    excludedBuildIds={excludedBuildIds}
                                    excludeReason={field.excludeReason}
                                />
                            </FormItem>
                        </React.Fragment>
                    );
                })}
                {props.renderRetainedBuildsBanner?.(
                    props.fields.reduce(
                        (acc, field) => {
                            acc[field.name] = toBuildIds(formValues?.[field.name]);
                            return acc;
                        },
                        {} as Record<string, string[]>,
                    ),
                )}
            </Form>
        </Modal>
    );
};

export default ChangeSelectedBuildModalBase;

const initialValueForField = (field: BuildSelectionField, availableBuilds: AppStoreAvailableBuild[]) => {
    const emptyValue = field.multiple ? [] : null;

    if (!field.currentSelection || field.currentSelection.length === 0) {
        return emptyValue;
    }

    const atLeastOneSelectedBuildAvailableInList = reduce(
        field.currentSelection,
        (buildAvailableInList, build) => {
            if (buildAvailableInList) {
                return true;
            }

            return !!availableBuilds.find(
                (availableBuild) => availableBuild.appStoreBuild.id === build.appStoreBuild.id,
            );
        },
        false,
    );

    if (!atLeastOneSelectedBuildAvailableInList) {
        return emptyValue;
    }

    const buildIds = field.currentSelection.map((build) => build.appStoreBuild.id);

    return field.multiple ? buildIds : buildIds[0];
};
